/**
 * MA-ARCH-137/150: Action Queue Component
 * 
 * Displays the full action queue with filtering capabilities.
 * Shows approved, pending, dismissed, and execution statuses.
 * 
 * MA-ARCH-150: Added execution workflow support
 * - Execute approved actions via Meta API
 * - Show dry_run, executing, success, failed statuses
 * - Display execution payloads and results
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  ListFilter,
  CheckCircle2,
  Clock,
  X,
  AlertTriangle,
  Loader2,
  TrendingUp,
  Wrench,
  PauseCircle,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  ArrowUpCircle,
  Copy,
  Flag,
  Zap,
  Target,
  FileText,
  Play,
  Calendar,
  User,
  RefreshCw,
  Rocket,
  TestTube,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  ChevronDown,
  ChevronRight,
  // MA-ARCH-159: Trace icons
  Network,
  GitBranch,
  Lightbulb,
  ListChecks,
  Eye,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';

// =============================================================================
// CONFIGURATION
// =============================================================================

const statusConfig = {
  proposed: {
    icon: Clock,
    label: 'Pending',
    bgClass: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  },
  approved: {
    icon: CheckCircle2,
    label: 'Approved',
    bgClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  },
  dismissed: {
    icon: X,
    label: 'Dismissed',
    bgClass: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  },
  ready_for_execution: {
    icon: Play,
    label: 'Ready',
    bgClass: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  },
  executed: {
    icon: CheckCircle,
    label: 'Executed',
    bgClass: 'bg-emerald-600/20 text-emerald-200 border-emerald-500/30',
  },
  execution_failed: {
    icon: XCircle,
    label: 'Failed',
    bgClass: 'bg-red-500/20 text-red-300 border-red-500/30',
  },
};

// MA-ARCH-150: Execution status configuration
const executionStatusConfig = {
  pending: {
    icon: Clock,
    label: 'Pending',
    bgClass: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    description: 'Awaiting execution',
  },
  executing: {
    icon: Loader2,
    label: 'Executing',
    bgClass: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    description: 'Currently processing',
    animate: true,
  },
  success: {
    icon: CheckCircle,
    label: 'Success',
    bgClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    description: 'Successfully executed on Meta',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    bgClass: 'bg-red-500/20 text-red-300 border-red-500/30',
    description: 'Execution failed',
  },
  dry_run: {
    icon: TestTube,
    label: 'Dry Run',
    bgClass: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    description: 'Validated without live API call',
  },
};

const typeConfig = {
  scale: {
    icon: TrendingUp,
    label: 'Scale',
    color: 'text-emerald-400',
  },
  fix: {
    icon: Wrench,
    label: 'Fix',
    color: 'text-amber-400',
  },
  pause: {
    icon: PauseCircle,
    label: 'Pause',
    color: 'text-red-400',
  },
};

const confidenceConfig = {
  high: { icon: ShieldCheck, color: 'text-emerald-400' },
  medium: { icon: ShieldAlert, color: 'text-amber-400' },
  low: { icon: ShieldQuestion, color: 'text-red-400' },
};

const actionTypeIcons = {
  increase_traffic: ArrowUpCircle,
  duplicate_campaign: Copy,
  mark_scaling_candidate: Flag,
  align_script: Zap,
  review_impulse_alignment: Target,
  review_show_rate: FileText,
  switch_script: FileText,
  pause_campaigns: PauseCircle,
  mark_pause_candidate: Flag,
  stop_traffic: X,
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function fetchActionQueue(params) {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.confidence) queryParams.append('confidence', params.confidence);
  if (params.recommendationType) queryParams.append('recommendation_type', params.recommendationType);
  if (params.workspaceId) queryParams.append('workspace_id', params.workspaceId);
  queryParams.append('limit', '50');
  
  const response = await api.get(`/actions/queue?${queryParams}`);
  return response.data;
}

async function executeAction(actionId, forceDryRun = false) {
  const response = await api.post('/action-execution/execute', {
    action_id: actionId,
    force_dry_run: forceDryRun,
  });
  return response.data;
}

async function fetchMetaConfigStatus() {
  const response = await api.get('/action-execution/meta-config-status');
  return response.data;
}

// =============================================================================
// EXECUTION STATUS BADGE
// =============================================================================

function ExecutionStatusBadge({ executionStatus, external_id }) {
  if (!executionStatus) return null;
  
  const config = executionStatusConfig[executionStatus] || executionStatusConfig.pending;
  const StatusIcon = config.icon;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border cursor-help",
            config.bgClass
          )}>
            <StatusIcon className={cn("h-3 w-3", config.animate && "animate-spin")} />
            <span>{config.label}</span>
            {external_id && executionStatus === 'success' && (
              <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{config.description}</p>
          {external_id && (
            <p className="text-xs text-slate-400 mt-1">
              Meta ID: {external_id}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// =============================================================================
// ACTION ROW COMPONENT
// =============================================================================

function ActionRow({ action, onExecute, isExecuting, metaConfigured }) {
  const [expanded, setExpanded] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const statusCfg = statusConfig[action.status] || statusConfig.proposed;
  const typeCfg = typeConfig[action.recommendation_type] || typeConfig.fix;
  const confCfg = confidenceConfig[action.confidence] || confidenceConfig.medium;
  const StatusIcon = statusCfg.icon;
  const TypeIcon = typeCfg.icon;
  const ConfIcon = confCfg.icon;
  const ActionIcon = actionTypeIcons[action.action_type] || Play;
  
  const createdAt = action.created_at ? new Date(action.created_at) : null;
  const approvedAt = action.approved_at ? new Date(action.approved_at) : null;
  const executedAt = action.executed_at ? new Date(action.executed_at) : null;
  
  // Can execute if approved and not already executing/succeeded
  const canExecute = action.status === 'approved' && 
    !['executing', 'success'].includes(action.execution_status);
  
  // Can re-execute if dry_run or failed
  const canReExecute = ['dry_run', 'failed'].includes(action.execution_status);
  
  return (
    <div 
      className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors"
      data-testid={`action-row-${action.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: Action info */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={cn("p-2 rounded", typeCfg.color, "bg-slate-800/60")}>
            <TypeIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={cn("text-xs font-medium uppercase", typeCfg.color)}>
                {typeCfg.label}
              </span>
              <span className="text-sm font-medium text-white truncate">
                {action.message_variant}
              </span>
              <span className={cn("flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border", statusCfg.bgClass)}>
                <StatusIcon className="h-3 w-3" />
                {statusCfg.label}
              </span>
              {/* MA-ARCH-150: Show execution status */}
              {action.execution_status && (
                <ExecutionStatusBadge 
                  executionStatus={action.execution_status}
                  external_id={action.external_id}
                />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
              <ActionIcon className="h-3 w-3" />
              <span className="truncate">{action.label}</span>
            </div>
            <p className="text-xs text-slate-500 line-clamp-1">{action.reason}</p>
            
            {/* Execution error */}
            {action.execution_error && action.execution_status === 'failed' && (
              <div className="mt-2 flex items-start gap-1.5 text-xs text-red-400 bg-red-500/10 p-2 rounded">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2">{action.execution_error}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Right: Metadata & Execute Button */}
        <div className="flex flex-col items-end gap-1.5 text-xs text-slate-400 flex-shrink-0">
          <div className="flex items-center gap-1" title={`Confidence: ${action.confidence}`}>
            <ConfIcon className={cn("h-3 w-3", confCfg.color)} />
            <span>{action.confidence}</span>
          </div>
          {createdAt && (
            <div className="flex items-center gap-1" title="Created">
              <Calendar className="h-3 w-3" />
              <span>{format(createdAt, 'MMM d, HH:mm')}</span>
            </div>
          )}
          {approvedAt && action.approved_by && (
            <div className="flex items-center gap-1 text-emerald-400" title={`Approved by ${action.approved_by}`}>
              <User className="h-3 w-3" />
              <span>{format(approvedAt, 'MMM d')}</span>
            </div>
          )}
          
          {/* MA-ARCH-150: Execute buttons */}
          {canExecute && (
            <div className="flex gap-1 mt-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                      onClick={() => onExecute(action.id, false)}
                      disabled={isExecuting === action.id}
                      data-testid={`execute-action-${action.id}`}
                    >
                      {isExecuting === action.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <Rocket className="h-3 w-3 mr-1" />
                          Execute
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      {metaConfigured 
                        ? 'Execute via Meta API' 
                        : 'Dry run (Meta not configured)'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {metaConfigured && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-violet-400 hover:bg-violet-500/20"
                        onClick={() => onExecute(action.id, true)}
                        disabled={isExecuting === action.id}
                      >
                        <TestTube className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Test with dry run</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          
          {/* Re-execute option for dry_run or failed */}
          {canReExecute && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-amber-400 hover:bg-amber-500/20 mt-1"
              onClick={() => onExecute(action.id, action.execution_status === 'dry_run')}
              disabled={isExecuting === action.id}
            >
              {isExecuting === action.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      
      {/* Profit path context & execution details */}
      <div className="mt-2 pt-2 border-t border-slate-700/30 space-y-1">
        {action.profit_path_name && (
          <span className="text-xs text-slate-500">
            Profit Path: <span className="text-slate-400">{action.profit_path_name}</span>
          </span>
        )}
        
        {/* MA-ARCH-150: Execution payload expandable section */}
        {(action.execution_payload || action.external_id) && (
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Execution details
            </button>
            {expanded && (
              <div className="mt-2 p-2 bg-slate-900/50 rounded text-xs font-mono">
                {action.external_id && (
                  <div className="mb-2">
                    <span className="text-slate-500">Meta ID: </span>
                    <span className="text-emerald-400">{action.external_id}</span>
                  </div>
                )}
                {executedAt && (
                  <div className="mb-2">
                    <span className="text-slate-500">Executed: </span>
                    <span className="text-slate-300">{format(executedAt, 'MMM d, yyyy HH:mm')}</span>
                  </div>
                )}
                {action.execution_payload && (
                  <div>
                    <span className="text-slate-500">Payload:</span>
                    <pre className="mt-1 text-slate-400 overflow-x-auto max-h-32">
                      {JSON.stringify(action.execution_payload, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* MA-ARCH-159: View Trace Button */}
        <button
          onClick={() => setShowTrace(true)}
          className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
          data-testid={`view-trace-${action.id}`}
        >
          <Network className="h-3 w-3" />
          View Trace
        </button>
      </div>
      
      {/* MA-ARCH-159: Action Trace Dialog */}
      <ActionTraceDialog 
        action={action} 
        open={showTrace} 
        onClose={() => setShowTrace(false)} 
      />
    </div>
  );
}

// =============================================================================
// META CONFIG STATUS BANNER
// =============================================================================

function MetaConfigBanner({ configStatus }) {
  if (!configStatus) return null;
  
  if (configStatus.configured) {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2 mb-4">
        <CheckCircle className="h-4 w-4" />
        <span>
          Meta API configured ({configStatus.config_source}) - Live execution enabled
        </span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 mb-4">
      <Info className="h-4 w-4" />
      <span>
        Meta API not configured - Actions will run in dry-run mode for validation
      </span>
    </div>
  );
}

// =============================================================================
// ACTION TRACE DIALOG - MA-ARCH-159
// =============================================================================

function ActionTraceDialog({ action, open, onClose }) {
  const { data: traceData, isLoading, error } = useQuery({
    queryKey: ['action-trace', action?.id],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/analytics/agent-performance/insights/actions/${action?.id}/trace`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Failed to fetch trace');
      return resp.json();
    },
    enabled: !!action?.id && open,
    staleTime: 30000,
  });
  
  if (!action) return null;
  
  const trace = traceData?.trace;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-xl max-h-[80vh] overflow-y-auto" data-testid="action-trace-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <Network className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <span className="text-slate-100">Action Trace</span>
              <p className="text-xs text-slate-400 font-normal mt-1">{action.id}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            <span className="ml-2 text-sm text-slate-400">Loading trace...</span>
          </div>
        )}
        
        {error && (
          <div className="text-center py-6">
            <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Unable to load trace</p>
          </div>
        )}
        
        {trace && (
          <div className="space-y-4 mt-4">
            {/* Provenance Status Warning */}
            {trace.provenance_warning && (
              <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
                <AlertTriangle className="h-3 w-3" />
                {trace.provenance_warning}
              </div>
            )}
            
            {/* Action Overview */}
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Zap className="h-3 w-3" />
                Action
              </h5>
              <div className="flex items-center gap-2">
                <Badge className="text-xs bg-indigo-500/20 border-indigo-500/30">
                  {trace.action?.type || action.action_type}
                </Badge>
                <Badge className={cn(
                  "text-xs border",
                  trace.action?.status === 'approved' ? "bg-emerald-500/20 border-emerald-500/30" :
                  trace.action?.status === 'proposed' ? "bg-amber-500/20 border-amber-500/30" :
                  "bg-slate-500/20 border-slate-500/30"
                )}>
                  {trace.action?.status || action.status}
                </Badge>
                {trace.action?.target_name && (
                  <span className="text-xs text-slate-400">
                    → {trace.action.target_name}
                  </span>
                )}
              </div>
            </div>
            
            {/* Source Provenance */}
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <GitBranch className="h-3 w-3" />
                Source Provenance
              </h5>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Created From</span>
                  <Badge className={cn(
                    "text-[10px] border",
                    trace.source?.source_mode === 'grouped' 
                      ? "bg-violet-500/20 border-violet-500/30" 
                      : "bg-slate-500/20 border-slate-500/30"
                  )}>
                    {trace.source?.source_mode === 'grouped' ? 'Grouped Batch' : 'Single Insight'}
                  </Badge>
                </div>
                {trace.source?.batch_id && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Batch ID</span>
                    <span className="text-slate-300 font-mono text-[10px]">{trace.source.batch_id}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Source Insights</span>
                  <span className="text-slate-300">{trace.provenance_summary?.insight_count || trace.source?.insight_ids?.length || 1}</span>
                </div>
              </div>
            </div>
            
            {/* Provenance Summary */}
            {trace.provenance_summary && (
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <ListChecks className="h-3 w-3" />
                  Aggregate Context
                </h5>
                <div className="space-y-2 text-xs">
                  {trace.provenance_summary.avg_confidence !== null && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Avg Confidence</span>
                      <span className={cn(
                        trace.provenance_summary.avg_confidence >= 0.7 ? "text-emerald-400" :
                        trace.provenance_summary.avg_confidence >= 0.5 ? "text-amber-400" : "text-red-400"
                      )}>
                        {(trace.provenance_summary.avg_confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                  {trace.provenance_summary.min_confidence !== null && trace.provenance_summary.min_confidence !== trace.provenance_summary.avg_confidence && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Min Confidence</span>
                      <span className="text-slate-300">{(trace.provenance_summary.min_confidence * 100).toFixed(0)}%</span>
                    </div>
                  )}
                  {trace.provenance_summary.primary_issue && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Primary Issue</span>
                      <span className="text-slate-300">{trace.provenance_summary.primary_issue.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {Object.keys(trace.provenance_summary.normalization_quality_distribution || {}).length > 0 && (
                    <div className="pt-2 border-t border-white/[0.05]">
                      <span className="text-slate-500">Normalization Quality:</span>
                      <div className="flex gap-2 mt-1">
                        {Object.entries(trace.provenance_summary.normalization_quality_distribution).map(([quality, count]) => (
                          <Badge key={quality} className="text-[9px] bg-slate-500/20 border-slate-500/30">
                            {quality.replace(/_/g, ' ')}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Review Context */}
            {trace.review_context && (
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-emerald-400" />
                  Review Context
                </h5>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Decision Mode</span>
                    <Badge className="text-[10px] bg-slate-500/20 border-slate-500/30">
                      {trace.review_context.decision_mode?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {trace.review_context.reviewer_names?.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Reviewed By</span>
                      <span className="text-slate-300">{trace.review_context.reviewer_names.join(', ')}</span>
                    </div>
                  )}
                  {trace.review_context.batch_size && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Batch Size</span>
                      <span className="text-slate-300">{trace.review_context.batch_size} insights</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Grouping Context (for batch) */}
            {trace.grouping_context && (
              <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                <h5 className="text-xs text-violet-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <ListChecks className="h-3 w-3" />
                  Grouping Context
                </h5>
                <p className="text-xs text-slate-300">{trace.grouping_context.group_reason}</p>
                {trace.grouping_context.validation_warnings?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {trace.grouping_context.validation_warnings.map((warning, i) => (
                      <p key={i} className="text-[10px] text-amber-400/70">⚠ {warning}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Source Insights */}
            {trace.source_snapshots?.length > 0 && (
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Lightbulb className="h-3 w-3" />
                  Source Insights ({trace.source_snapshots.length})
                </h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {trace.source_snapshots.map((snapshot, i) => (
                    <div key={snapshot.insight_id || i} className="p-2 bg-slate-800/50 rounded text-xs">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-slate-300 flex-1">{snapshot.headline || 'Unknown'}</span>
                        {snapshot.confidence_score && (
                          <Badge className={cn(
                            "text-[9px] border",
                            snapshot.confidence_score >= 0.7 ? "bg-emerald-500/20 border-emerald-500/30" :
                            snapshot.confidence_score >= 0.5 ? "bg-amber-500/20 border-amber-500/30" :
                            "bg-red-500/20 border-red-500/30"
                          )}>
                            {(snapshot.confidence_score * 100).toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {snapshot.insight_type && (
                          <span className="text-[10px] text-slate-500">{snapshot.insight_type.replace(/_/g, ' ')}</span>
                        )}
                        {snapshot.severity && (
                          <span className="text-[10px] text-slate-500">• {snapshot.severity}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* History Summary */}
            {trace.history_summary?.length > 0 && (
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  Timeline
                </h5>
                <div className="space-y-1">
                  {trace.history_summary.map((milestone, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">{milestone.actor}:</span>
                      <span className="text-slate-300 flex-1">{milestone.summary}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {!isLoading && !error && !trace && (
          <div className="text-center py-6">
            <Network className="h-8 w-8 text-slate-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Trace data unavailable</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ActionQueue({ workspaceId }) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [executingActionId, setExecutingActionId] = useState(null);
  
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['action-queue', statusFilter, typeFilter, confidenceFilter, workspaceId],
    queryFn: () => fetchActionQueue({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      recommendationType: typeFilter !== 'all' ? typeFilter : undefined,
      confidence: confidenceFilter !== 'all' ? confidenceFilter : undefined,
      workspaceId,
    }),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
  
  // MA-ARCH-150: Fetch Meta config status
  const { data: metaConfigStatus } = useQuery({
    queryKey: ['meta-config-status'],
    queryFn: fetchMetaConfigStatus,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  
  // MA-ARCH-150: Execute action mutation
  const executeActionMutation = useMutation({
    mutationFn: ({ actionId, forceDryRun }) => executeAction(actionId, forceDryRun),
    onMutate: ({ actionId }) => {
      setExecutingActionId(actionId);
    },
    onSuccess: (result) => {
      const isLive = result.execution_mode === 'live';
      const statusEmoji = result.success ? '✓' : '✗';
      
      if (result.execution_status === 'dry_run') {
        toast.info(`${statusEmoji} Dry run completed`, {
          description: 'Action validated without live API call',
        });
      } else if (result.success) {
        toast.success(`${statusEmoji} Action executed`, {
          description: result.external_id 
            ? `Meta ID: ${result.external_id}` 
            : 'Action completed successfully',
        });
      } else {
        toast.error('Execution failed', {
          description: result.error_message || 'Unknown error occurred',
        });
      }
      
      // Refetch to update the list
      refetch();
    },
    onError: (error) => {
      toast.error('Execution failed', {
        description: error.response?.data?.detail || error.message,
      });
    },
    onSettled: () => {
      setExecutingActionId(null);
    },
  });
  
  const handleExecute = (actionId, forceDryRun) => {
    executeActionMutation.mutate({ actionId, forceDryRun });
  };
  
  const { actions = [], summary = {} } = data || {};
  
  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading action queue...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardContent className="py-12 text-center">
          <ListFilter className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">Action Queue Unavailable</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Unable to load action queue at this time. This may be due to insufficient permissions or missing data.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-slate-900/50 border-slate-700/50" data-testid="action-queue">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ListFilter className="h-4 w-4 text-violet-400" />
              Action Queue
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Approve, execute, and track action statuses
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Summary badges */}
            <div className="flex items-center gap-2 text-xs mr-2">
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />
                {summary.approved || 0}
              </span>
              <span className="flex items-center gap-1 text-amber-400">
                <Clock className="h-3 w-3" />
                {summary.proposed || 0}
              </span>
              <span className="flex items-center gap-1 text-slate-400">
                <X className="h-3 w-3" />
                {summary.dismissed || 0}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-8 px-2"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2 mt-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs bg-slate-800 border-slate-700">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="proposed">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="executed">Executed</SelectItem>
              <SelectItem value="execution_failed">Failed</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-28 h-8 text-xs bg-slate-800 border-slate-700">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="scale">Scale</SelectItem>
              <SelectItem value="fix">Fix</SelectItem>
              <SelectItem value="pause">Pause</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
            <SelectTrigger className="w-32 h-8 text-xs bg-slate-800 border-slate-700">
              <SelectValue placeholder="Confidence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Confidence</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* MA-ARCH-150: Meta config status banner */}
        <MetaConfigBanner configStatus={metaConfigStatus} />
        
        {actions.length === 0 ? (
          <div className="text-center py-8">
            <ListFilter className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No actions found</p>
            <p className="text-xs text-slate-500 mt-1">
              {statusFilter !== 'all' || typeFilter !== 'all' || confidenceFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Actions will appear here when recommendations are generated'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {actions.map((action) => (
              <ActionRow 
                key={action.id} 
                action={action}
                onExecute={handleExecute}
                isExecuting={executingActionId}
                metaConfigured={metaConfigStatus?.configured}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
